import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, Loader2, Award, FileText, ArrowLeft, Sparkles, HelpCircle,
  Brain, FileCheck, Lightbulb, X, ChevronRight, PlayCircle, BookOpen, ListChecks,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { academyAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function ProgressRing({ pct }) {
  const r = 18, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative w-12 h-12" data-testid="course-progress-ring">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle cx="22" cy="22" r={r} fill="none" stroke="#0d9488" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700">{pct}%</span>
    </div>
  );
}

const AI_ACTIONS = [
  { id: 'summarize', label: 'Summarize', Icon: Sparkles },
  { id: 'explain', label: 'Explain', Icon: HelpCircle },
  { id: 'quiz_me', label: 'Quiz me', Icon: Brain },
  { id: 'practice_test', label: 'Practice test', Icon: FileCheck },
  { id: 'remember', label: 'Key takeaways', Icon: Lightbulb },
];

export default function CoursePlayerPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enr, setEnr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [helperOpen, setHelperOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiAction, setAiAction] = useState('');

  const load = useCallback(async (keepActive = false) => {
    if (!keepActive) setLoading(true);
    try {
      const r = await academyAPI.getCourse(courseId);
      setCourse(r.data.course);
      setEnr(r.data.enrollment || null);
      if (!keepActive) {
        const done = new Set(r.data.enrollment?.completed_lessons || []);
        const flat = [];
        (r.data.course.modules || []).forEach((m) => (m.lessons || []).forEach((l) => flat.push({ moduleId: m.module_id, lessonId: l.lesson_id })));
        const target = flat.find((l) => !done.has(l.lessonId)) || flat[0];
        if (target) setActive({ type: 'lesson', moduleId: target.moduleId, lessonId: target.lessonId });
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load course'));
    } finally {
      if (!keepActive) setLoading(false);
    }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const completed = useMemo(() => new Set(enr?.completed_lessons || []), [enr]);
  const perModule = (course?.exam_mode || 'final') === 'per_module';

  const allLessons = useMemo(() => {
    const out = [];
    (course?.modules || []).forEach((m) => (m.lessons || []).forEach((l) => out.push({ moduleId: m.module_id, lessonId: l.lesson_id, title: l.title })));
    return out;
  }, [course]);
  const totalLessons = allLessons.length;
  const doneLessons = allLessons.filter((l) => completed.has(l.lessonId)).length;
  const progressPct = enr?.passed ? 100 : (totalLessons ? Math.round((doneLessons / totalLessons) * 100) : 0);
  const nextLesson = allLessons.find((l) => !completed.has(l.lessonId));

  const markComplete = async (lessonId) => {
    try {
      const r = await academyAPI.completeLesson(courseId, lessonId);
      setEnr((e) => ({ ...(e || {}), completed_lessons: r.data.completed_lessons }));
    } catch (_) { /* noop */ }
  };

  const submitExam = async (scope) => {
    setSubmitting(true);
    try {
      const r = await academyAPI.submitExam(courseId, { scope, answers });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed with ${r.data.score}%`);
      else toast.error(`Score ${r.data.score}% — need ${course.pass_score}%`);
      await load(true);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not submit exam'));
    } finally {
      setSubmitting(false);
    }
  };

  const runAI = async (actionId) => {
    setAiAction(actionId); setAiLoading(true); setAiText(''); setHelperOpen(true);
    try {
      const payload = { action: actionId };
      if (active?.type === 'lesson') { payload.module_id = active.moduleId; payload.lesson_id = active.lessonId; }
      const r = await academyAPI.ai(courseId, payload);
      setAiText(r.data.text || 'No response.');
    } catch (e) {
      setAiText('');
      toast.error(getErrorMessage(e, 'AI helper is unavailable right now'));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !course) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-400" /></div>;

  const openExam = (scope) => { setActive({ type: 'exam', scope }); setAnswers({}); setResult(null); };
  const activeModule = course.modules?.find((m) => m.module_id === active?.moduleId);
  const activeLesson = activeModule?.lessons?.find((l) => l.lesson_id === active?.lessonId);
  const examQuestions = active?.type === 'exam'
    ? (active.scope === 'final' ? (course.final_exam?.questions || []) : (course.modules.find((m) => m.module_id === active.scope)?.quiz?.questions || []))
    : [];

  return (
    <div data-testid="course-player-page">
      <div className="page-content">
        <button onClick={() => navigate('/academy')} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-3" data-testid="course-back"><ArrowLeft size={15} /> Back to Academy</button>

        {/* Course header */}
        <div className="rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-4 mb-4 flex items-center justify-between gap-4" data-testid="course-header">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] text-slate-300">
              <span>{course.category || 'General'}</span>
              {course.required && <span className="rounded-full bg-rose-500/90 px-2 py-0.5 text-[9px] font-bold uppercase">Required</span>}
            </div>
            <h1 className="text-xl font-bold truncate">{course.title}</h1>
            <div className="flex items-center gap-3 text-[11px] text-slate-300 mt-1">
              <span className="inline-flex items-center gap-1"><BookOpen size={12} /> {totalLessons} lessons</span>
              <span className="inline-flex items-center gap-1"><ListChecks size={12} /> Pass {course.pass_score}%</span>
              {enr?.passed
                ? <Badge className="bg-emerald-500/20 text-emerald-200 border-0 text-[10px]"><Award size={11} className="mr-1" /> Completed</Badge>
                : nextLesson && <span className="inline-flex items-center gap-1 text-teal-300">Next: {nextLesson.title}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="bg-white/90 rounded-full p-0.5"><ProgressRing pct={progressPct} /></div>
            {enr?.passed && course.gives_certificate && enr.certificate_id && (
              <Button size="sm" variant="outline" className="bg-white text-slate-800" onClick={() => navigate(`/academy/certificate/${enr.certificate_id}`)} data-testid="course-view-cert"><FileText size={13} className="mr-1" /> Certificate</Button>
            )}
          </div>
        </div>

        <div className={`grid gap-5 ${helperOpen ? 'lg:grid-cols-[260px_1fr_320px]' : 'lg:grid-cols-[260px_1fr]'}`}>
          {/* Sidebar: curriculum */}
          <Card className="h-fit">
            <CardContent className="p-3 space-y-3" data-testid="course-curriculum">
              {(course.modules || []).map((m, mi) => {
                const mLessons = m.lessons || [];
                const mDone = mLessons.filter((l) => completed.has(l.lesson_id)).length;
                return (
                  <div key={m.module_id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-semibold text-slate-700">{mi + 1}. {m.title}</div>
                      <span className="text-[10px] text-slate-400">{mDone}/{mLessons.length}</span>
                    </div>
                    <div className="space-y-0.5">
                      {mLessons.map((l) => (
                        <button key={l.lesson_id}
                          onClick={() => { setActive({ type: 'lesson', moduleId: m.module_id, lessonId: l.lesson_id }); setResult(null); }}
                          className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-center gap-2 ${active?.lessonId === l.lesson_id ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}
                          data-testid={`lesson-nav-${l.lesson_id}`}>
                          {completed.has(l.lesson_id) ? <CheckCircle2 size={13} className="text-emerald-500 shrink-0" /> : <Circle size={13} className="text-slate-300 shrink-0" />}
                          <span className="truncate">{l.title}</span>
                        </button>
                      ))}
                      {perModule && (m.quiz?.questions || []).length > 0 && (
                        <button onClick={() => openExam(m.module_id)} className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-center gap-2 ${active?.scope === m.module_id ? 'bg-indigo-50 font-medium' : 'hover:bg-slate-50'}`} data-testid={`module-exam-${m.module_id}`}>
                          <FileText size={13} className="text-indigo-500 shrink-0" /> Module quiz {enr?.passed_scopes?.includes(m.module_id) ? '✓' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!perModule && (course.final_exam?.questions || []).length > 0 && (
                <button onClick={() => openExam('final')} className={`w-full text-left text-sm px-2 py-2 rounded-md flex items-center gap-2 border-t pt-3 ${active?.scope === 'final' ? 'bg-indigo-50 font-medium' : 'hover:bg-slate-50'}`} data-testid="final-exam-nav">
                  <FileText size={14} className="text-indigo-500" /> Final exam {enr?.passed_scopes?.includes('final') ? '✓' : ''}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Main content */}
          <div className="min-w-0">
            {active?.type === 'lesson' && activeLesson && (
              <Card><CardContent className="p-5 space-y-4" data-testid="lesson-content">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] text-slate-400">{activeModule?.title}</div>
                    <h2 className="text-lg font-semibold text-slate-800">{activeLesson.title}</h2>
                  </div>
                  <Button size="sm" variant="outline" className="shrink-0 border-teal-200 text-teal-700 hover:bg-teal-50" onClick={() => runAI('summarize')} data-testid="ai-helper-open"><Sparkles size={14} className="mr-1" /> Study Helper</Button>
                </div>
                {ytEmbed(activeLesson.video_url) && (
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                    <iframe title={activeLesson.title} src={ytEmbed(activeLesson.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                )}
                {activeLesson.content && <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{activeLesson.content}</div>}
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                  <Button onClick={() => markComplete(activeLesson.lesson_id)} disabled={completed.has(activeLesson.lesson_id)} data-testid="lesson-complete-btn">
                    {completed.has(activeLesson.lesson_id) ? <><CheckCircle2 size={15} className="mr-1" /> Completed</> : 'Mark as complete'}
                  </Button>
                  {nextLesson && nextLesson.lessonId !== activeLesson.lesson_id && (
                    <Button variant="outline" onClick={() => { setActive({ type: 'lesson', moduleId: nextLesson.moduleId, lessonId: nextLesson.lessonId }); }} data-testid="lesson-next-btn">
                      Next lesson <ChevronRight size={15} className="ml-1" />
                    </Button>
                  )}
                </div>
              </CardContent></Card>
            )}

            {active?.type === 'exam' && (
              <Card><CardContent className="p-5 space-y-5" data-testid="exam-content">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><ListChecks size={18} className="text-indigo-600" /> {active.scope === 'final' ? 'Final exam' : 'Module quiz'}</h2>
                  <span className="text-xs text-slate-500">Pass: {course.pass_score}%{course.max_attempts ? ` · ${course.max_attempts} attempts` : ' · unlimited attempts'}</span>
                </div>
                {result && (
                  <div className={`rounded-lg p-3 text-sm ${result.passed ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`} data-testid="exam-result">
                    <div className="flex items-center gap-2 font-semibold">{result.passed ? <CheckCircle2 size={16} /> : null} You scored {result.score}% ({result.correct}/{result.total}). {result.passed ? 'Passed!' : `You need ${course.pass_score}%.`}</div>
                    {result.course_passed && (
                      <div className="mt-2 flex items-center gap-2"><Award size={15} className="text-amber-500" /> Course completed — badge earned!
                        {result.certificate_id && <Button size="sm" variant="outline" className="ml-1 h-7" onClick={() => navigate(`/academy/certificate/${result.certificate_id}`)} data-testid="exam-view-cert"><FileText size={12} className="mr-1" /> Certificate</Button>}
                      </div>
                    )}
                  </div>
                )}
                {examQuestions.map((q, qi) => (
                  <div key={q.question_id} className="border border-slate-100 rounded-lg p-3" data-testid={`exam-q-${qi}`}>
                    <div className="text-sm font-medium text-slate-800 mb-2">{qi + 1}. {q.prompt}</div>
                    {q.type === 'tf' ? (
                      <div className="flex gap-2">
                        {[true, false].map((v) => (
                          <button key={String(v)} onClick={() => setAnswers((a) => ({ ...a, [q.question_id]: v }))}
                            className={`px-4 py-1.5 rounded-md text-sm border ${answers[q.question_id] === v ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 hover:bg-slate-50'}`}
                            data-testid={`exam-q-${qi}-${v ? 'true' : 'false'}`}>{v ? 'True' : 'False'}</button>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {(q.options || []).map((o) => {
                          const sel = (answers[q.question_id] || []).includes(o.id);
                          return (
                            <button key={o.id} onClick={() => setAnswers((a) => {
                              const cur = new Set(a[q.question_id] || []);
                              if (cur.has(o.id)) cur.delete(o.id); else cur.add(o.id);
                              return { ...a, [q.question_id]: Array.from(cur) };
                            })} className={`w-full text-left text-sm px-3 py-1.5 rounded-md border ${sel ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 hover:bg-slate-50'}`}
                              data-testid={`exam-q-${qi}-opt-${o.id}`}>{o.text}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
                <Button onClick={() => submitExam(active.scope)} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="exam-submit-btn">
                  {submitting ? <Loader2 size={15} className="mr-1 animate-spin" /> : null} Submit exam
                </Button>
              </CardContent></Card>
            )}
          </div>

          {/* AI Study Helper */}
          {helperOpen && (
            <div className="rounded-xl bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 border border-teal-800/40 text-teal-100 p-4 h-fit lg:sticky lg:top-4" data-testid="ai-helper-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} className="text-teal-400" /> Study Helper</div>
                <button onClick={() => setHelperOpen(false)} className="text-teal-300 hover:text-white" data-testid="ai-helper-close"><X size={16} /></button>
              </div>
              <p className="text-[11px] text-teal-300/80 mb-3">AI study support based on this course. It never changes official exam answers or grades.</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {AI_ACTIONS.map(({ id, label, Icon }) => (
                  <button key={id} onClick={() => runAI(id)} disabled={aiLoading}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors ${aiAction === id ? 'bg-teal-500/20 border-teal-500 text-white' : 'border-teal-800/60 hover:bg-teal-900/40'}`}
                    data-testid={`ai-action-${id}`}>
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
              <div className="rounded-lg bg-slate-950/50 border border-teal-900/40 p-3 min-h-[120px] max-h-[460px] overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap" data-testid="ai-helper-output">
                {aiLoading ? (
                  <span className="flex items-center gap-2 text-teal-300"><Loader2 size={13} className="animate-spin" /> Thinking…</span>
                ) : (aiText || <span className="text-teal-300/60">Pick an action above to get study help for this lesson.</span>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
